// stores 
import * as ProtocolConsumptionStore from '../../../redux/middleware/ProtocolConsumptionMiddleware';
import * as ProtocolStore from '../../../redux/middleware/MedicalProtocolMiddleware';
import * as ContractConceptStore from '../../../redux/middleware/ContractConceptMiddleware';
import * as ContractAddendumStore from '../../../redux/middleware/ContractAddendum.Middleware';
import * as ContractStore from '../../../redux/middleware/ContractMiddleware';

// state
import { AppState } from '../../../redux/reducers';
import { ProtocolConsumption } from '../../../models/Entities/Protocols/ProtocolConsumption';
import { Protocol } from '../../../models/Entities/Protocols/Protocol';
import { Practitioner } from '../../../models/Entities/Practitioners/Practitioner';
import { ErrorType } from '../../../models/HttpError';
import { ProtocolConsumptionVisit } from '../../../models/Entities/Protocols/ProtocolConsumptionVisit';
import { Contract } from '../../../models/Entities/Contracts/Contract';
import { ContractConcept } from '../../../models/Entities/Contracts/ContractConcept';
import { ContractStage } from '../../../models/Entities/Contracts/ContractStage';

//#region Props

interface IProps {
    ProtocolId: number;
    ProtocolConsumptionId: string | null;
    ParentProtocolConsumptionId: string | undefined;
    visitId: string | undefined;
    isOpen: boolean;
    onClose: () => void;
}

interface ConnectedProps {
    // protocolo dueño del consumo (el detalle lo busca dentro del protocolo en el store)
    ProtocolLoaded: boolean;
    failLoadingProtocol: boolean;

    // loading
    isLoading: boolean;
    successLoading: boolean | undefined;
    failLoading: boolean;
    error: ErrorType | null;

    // changing concept
    isChangingConcept: boolean;
    successChangingConcept: boolean | undefined;
    failChangingConcept: boolean;
    errorChangingConcept: ErrorType | null;

    // changing producer 
    isChangingProducer: boolean;
    successChangingProducer: boolean | undefined;
    failChangingProducer: boolean;
    errorChangingProducer: ErrorType | null;

    // changing provider
    isChangingProvider: boolean;
    successChangingProvider: boolean | undefined;
    failChangingProvider: boolean;
    errorChangingProvider: ErrorType | null;

    // changing medical service
    isChangingMedicalService: boolean;
    successChangingMedicalService: boolean | undefined;
    failChangingMedicalService: boolean;
    errorChangingMedicalService: ErrorType | null;

    Consumption: ProtocolConsumption | null;
    ContractId: number | undefined;
    ContractAddendumId: number | undefined;
    CurrentConcepts: ContractConcept[];
    CurrentStages: ContractStage[];
    AddendumLoaded: boolean;
    ContractLoaded: boolean;
    GetPersonByIdentifier: (identifier: string) => Practitioner | undefined;
}

interface MedicalServicesDispatchProps {
    GetMedicalProtocolById: typeof ProtocolStore.actionCreators.GetMedicalProtocolById;
    GetProtocolConsumptionById: typeof ProtocolConsumptionStore.ActionCreators.GetProtocolConsumptionById;
    CreateProtocolConsumption: typeof ProtocolConsumptionStore.ActionCreators.CreateProtocolConsumption;
    UpdateProtocolConsumption: typeof ProtocolConsumptionStore.ActionCreators.UpdateProtocolConsumption;
    ChangeVisit: typeof ProtocolConsumptionStore.ActionCreators.ChangeVisit;
    DeleteProtocolConsumption: typeof ProtocolConsumptionStore.ActionCreators.DeleteProtocolConsumption;
    ChangeContractConcept: typeof ProtocolConsumptionStore.ActionCreators.ChangeContractConcept;
    ChangeProducer: typeof ProtocolConsumptionStore.ActionCreators.ChangeProducer;
    ChangeProvider: typeof ProtocolConsumptionStore.ActionCreators.ChangeProvider;
    ChangeMedicalService: typeof ProtocolConsumptionStore.ActionCreators.ChangeMedicalService;
    ChangeProcedure: typeof ProtocolConsumptionStore.ActionCreators.ChangeProcedure;
    ChangeAdditional: typeof ProtocolConsumptionStore.ActionCreators.ChangeAdditional;
    SetConsumptionEarlyDiscontinuation: typeof ProtocolConsumptionStore.ActionCreators.SetConsumptionEarlyDiscontinuation;
    SetConsumptionCondition: typeof ProtocolConsumptionStore.ActionCreators.SetConsumptionCondition;
    SetConsumptionGrouping: typeof ProtocolConsumptionStore.ActionCreators.SetConsumptionGrouping;
    MarkConsumptionFollowUp: typeof ProtocolConsumptionStore.ActionCreators.MarkConsumptionFollowUp;
    GetAllProtocolContractAssociations: typeof ProtocolStore.actionCreators.GetAllProtocolContractAssociations;
    GetContractConceptById: typeof ContractConceptStore.actionCreators.GetContractConceptById;
    GetAllContractsConceptByAddendum: typeof ContractConceptStore.actionCreators.GetAllContractsConceptByAddendum;
    GetContractAddendumsByContractId: typeof ContractAddendumStore.actionCreators.GetContractAddendumsByContractId;
    GetContractById: typeof ContractStore.actionCreators.GetContractById;
}

export type Props = IProps & ConnectedProps & MedicalServicesDispatchProps;

export const mapStateToProps = (state: AppState, ownProps: IProps) => {

    const practitioners = state.Practitioners?.list || [];

    const AuxProtocol: Protocol | null = state.MedicalProtocols?.list?.find((protocol: Protocol) => protocol.id === ownProps.ProtocolId) || null;

    const AuxConsumption: ProtocolConsumption | null = ownProps.ParentProtocolConsumptionId
        ? (
            // full data (fetched by GetProtocolConsumptionById) lands in the flat consumptions list
            AuxProtocol?.consumptions?.find((consumption: ProtocolConsumption) => consumption.id === ownProps.ProtocolConsumptionId)
            // fallback: partial data embedded in parent's childConsumption
            ?? AuxProtocol?.consumptions?.find((consumption: ProtocolConsumption) => consumption.id === ownProps.ParentProtocolConsumptionId)
                ?.childConsumption?.find((consumption: ProtocolConsumption) => consumption.id === ownProps.ProtocolConsumptionId)
            ?? null
        )
        : AuxProtocol?.consumptions?.find((consumption: ProtocolConsumption) => consumption.id === ownProps.ProtocolConsumptionId) ??
        AuxProtocol?.ConsumptionVisits?.find((visit: ProtocolConsumptionVisit) => visit.id === ownProps.visitId)?.consumptions?.find((consumption: ProtocolConsumption) => consumption.id === ownProps.ProtocolConsumptionId) ?? null;

    const AuxContractId: number | undefined = AuxProtocol?.contractAssociation?.contractId ?? undefined;

    // ContractConcept carries its own contractAddendumId, so read it directly from the
    // dedicated ContractConcept slice (fetched via GetContractConceptById) instead of
    // guessing an "active" addendum on the Contract.
    const AuxContractConcept: ContractConcept | undefined = AuxConsumption?.contractConceptId
        ? state.ContractConcept?.list?.find((concept: ContractConcept) => concept?.id === AuxConsumption.contractConceptId)
        : undefined;

    const AuxContractAddendumId: number | undefined = AuxContractConcept?.contractAddendumId;

    // The addendum-concepts reducer silently no-ops unless the addendum already exists
    // in Contract.addendums, so track when that rehydration has completed.
    const AuxContract: Contract | undefined = AuxContractId
        ? state.Contract?.list?.find((contract: Contract) => contract.id === AuxContractId)
        : undefined;
    const AuxAddendumLoaded: boolean = !!AuxContract?.addendums?.some((addendum) => addendum.id === AuxContractAddendumId);

    return {
        ProtocolLoaded: AuxProtocol !== null,
        failLoadingProtocol: state.MedicalProtocols?.failOnLoadingOne || false,

        // loading
        isLoading: state.MedicalProtocols?.isLoadingOneConsumption || false,
        successLoading: state.MedicalProtocols?.LoadingOneConsumptionSuccess || undefined,
        failLoading: state.MedicalProtocols?.FailGettingOneConsumption || null,
        error: state.MedicalProtocols?.ErrorGettingOneConsumption || null,

        // changing concept
        isChangingConcept: AuxConsumption?.isConcepAssociationChanging ?? false,
        successChangingConcept: AuxConsumption?.conceptAssociationChanged ?? undefined,
        failChangingConcept: AuxConsumption?.failonConceptAssociationChange ?? false,
        errorChangingConcept: AuxConsumption?.ErrorOnConceptAssociationChange ?? null,

        // changing producer
        isChangingProducer: AuxConsumption?.isProducerChanging ?? false,
        successChangingProducer: AuxConsumption?.producerChanged ?? undefined,
        failChangingProducer: AuxConsumption?.failonProducerChange ?? false,
        errorChangingProducer: AuxConsumption?.ErrorOnProducerChange ?? null,

        // changing provider
        isChangingProvider: AuxConsumption?.isProviderChanging ?? false,
        successChangingProvider: AuxConsumption?.providerChanged ?? undefined,
        failChangingProvider: AuxConsumption?.failonProviderChange ?? false,
        errorChangingProvider: AuxConsumption?.ErrorOnProviderChange ?? null,

        // changing medical service
        isChangingMedicalService: AuxConsumption?.isMedicalServiceChanging ?? false,
        successChangingMedicalService: AuxConsumption?.medicalServiceChanged ?? undefined,
        failChangingMedicalService: AuxConsumption?.failonMedicalServiceChange ?? false,
        errorChangingMedicalService: AuxConsumption?.ErrorOnMedicalServiceChange,

        Consumption: AuxConsumption || null,
        ContractId: AuxContractId,
        ContractAddendumId: AuxContractAddendumId,
        CurrentConcepts: [],
        CurrentStages: [],
        AddendumLoaded: AuxAddendumLoaded,
        ContractLoaded: AuxContract !== undefined,
        GetPersonByIdentifier: (identifier: string) => GetPersonByIdentifier(practitioners, identifier)
    }
};

export const mapDispatchToProps = {
    ...ProtocolConsumptionStore.ActionCreators,
    ...ProtocolStore.actionCreators,
    ...ContractConceptStore.actionCreators,
    ...ContractAddendumStore.actionCreators,
    ...ContractStore.actionCreators
};

export const GetStatusDescription = (status: number) => {

    /*
        NotSettled = 0,
        Pending = 1,
        Payee = 2,
        Settled = 3,
        Rejected = 4,
        Ignored = 5,
     */

    switch (status) {
        case 0:
            return 'Pendiente de liquidación';
        case 1:
            return 'Con saldo pendiente';
        case 2:
            return 'Pagado';
        case 3:
            return 'Liquidado';
        case 4:
            return 'Rechazado';
        case 5:
            return 'Ignorado';
        default:
            return 'Unknown';
    }
};

// get person by identifier
const GetPersonByIdentifier = (persons: Practitioner[], identifier: string): Practitioner | undefined => {
    return persons.find((person: Practitioner) => person.userId === identifier);
}